/**
 * Transcript Overflow Menu Controller
 * Handles the mobile overflow menu (⋮) for transcript actions.
 * Shows/hides panel, delegates action clicks to existing global handlers.
 */

export function createTranscriptOverflow({ document, window }) {
  let overflowBtn = null;
  let overflowPanel = null;
  let isOpen = false;
  let isInitialized = false;

  function init() {
    if (isInitialized) return;

    overflowBtn = document.getElementById("transcriptOverflowBtn");
    overflowPanel = document.getElementById("transcriptOverflowPanel");

    if (!overflowBtn || !overflowPanel) {
      return;
    }

    overflowBtn.addEventListener("click", toggle);
    overflowPanel.addEventListener("click", handleActionClick);
    document.addEventListener("click", handleOutsideClick);
    document.addEventListener("keydown", handleKeyDown);

    isInitialized = true;
  }

  function toggle(event) {
    if (event) {
      event.stopPropagation();
    }
    if (isOpen) {
      close();
    } else {
      open();
    }
  }

  function open() {
    if (!overflowBtn || !overflowPanel || isOpen) return;

    isOpen = true;
    overflowPanel.classList.add("open");
    overflowBtn.setAttribute("aria-expanded", "true");

    // Sync draft actions visibility with container state
    syncDraftActions();
  }

  function close() {
    if (!overflowBtn || !overflowPanel || !isOpen) return;

    isOpen = false;
    overflowPanel.classList.remove("open");
    overflowBtn.setAttribute("aria-expanded", "false");
  }

  function syncDraftActions() {
    const container = document.getElementById("transcriptView");
    const isDraft = container && container.classList.contains("is-draft");
    const draftActions = overflowPanel.querySelectorAll("[data-draft-only]");
    const defaultActions = overflowPanel.querySelectorAll(
      "[data-action='copy'], [data-action='download']",
    );

    draftActions.forEach((action) => {
      action.hidden = !isDraft;
    });

    defaultActions.forEach((action) => {
      action.hidden = isDraft;
    });
  }

  function handleActionClick(event) {
    const actionBtn = event.target.closest("[data-action]");
    if (!actionBtn) return;

    const action = actionBtn.dataset.action;

    // Close menu first
    close();

    // Delegate to existing global handlers
    switch (action) {
      case "copy":
        if (typeof window.copyCurrentTranscript === "function") {
          window.copyCurrentTranscript();
        }
        break;
      case "download":
        if (typeof window.downloadCurrentTranscript === "function") {
          window.downloadCurrentTranscript();
        }
        break;
      case "save":
        if (typeof window.saveCurrentTranscript === "function") {
          window.saveCurrentTranscript();
        }
        break;
      case "discard":
        if (typeof window.discardCurrentTranscript === "function") {
          window.discardCurrentTranscript();
        }
        break;
    }
  }

  function handleOutsideClick(event) {
    if (!isOpen) return;

    // Close if click is outside the overflow menu
    const overflowMenu = document.getElementById("transcriptOverflowMenu");
    if (overflowMenu && !overflowMenu.contains(event.target)) {
      close();
    }
  }

  function handleKeyDown(event) {
    if (!isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  }

  function destroy() {
    if (!isInitialized) return;

    if (overflowBtn) {
      overflowBtn.removeEventListener("click", toggle);
    }
    if (overflowPanel) {
      overflowPanel.removeEventListener("click", handleActionClick);
    }
    document.removeEventListener("click", handleOutsideClick);
    document.removeEventListener("keydown", handleKeyDown);

    overflowBtn = null;
    overflowPanel = null;
    isOpen = false;
    isInitialized = false;
  }

  return {
    init,
    open,
    close,
    syncDraftActions,
    destroy,
    isOpen: () => isOpen,
  };
}
